/**
 * StreamView tab (morphs issue #9): live preview + editor for the OBS overlay
 * page. Drag the "now playing" text and the video into any corner (fixed
 * preset margins — not configurable); size/fade the video and resize the
 * now-playing text via sliders; pick a font; copy/regenerate the public OBS URL.
 *
 * The URL is provisioned automatically the first time a streamer lands here
 * (no token yet ⇒ silently mint one) — only an explicit *re*-generate needs a
 * confirm, since that's the one that breaks an already-pasted OBS source.
 *
 * `nowPlaying` for the preview comes from the already-authed `useQueue` hook
 * (same data the Queue tab shows) rather than hitting the public /overlay
 * endpoint — no reason to round-trip through the unauthenticated path from
 * inside the authed app.
 */
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, ApiError, type Settings as StreamSettings } from "../lib/api";
import { useQueue } from "../hooks/useQueue";
import type { ChannelContext } from "../components/Layout";
import {
  OverlayCanvas,
  MIN_VIDEO_WIDTH,
  MAX_VIDEO_WIDTH,
  MIN_NOW_PLAYING_WIDTH,
  MAX_NOW_PLAYING_WIDTH,
  type OverlayLayout,
} from "../components/OverlayCanvas";
import { OVERLAY_FONTS } from "../lib/overlayFonts";
import { StreamViewSkeleton } from "../components/Skeleton";

export function StreamView() {
  const { channelId } = useOutletContext<ChannelContext>();
  const { data: queue } = useQueue(channelId);
  const [settings, setSettings] = useState<StreamSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  // Live slider position while dragging — committed to the API on release
  // (onChange fires on every tick of the drag; we only want one PUT per drag).
  const [draftOpacity, setDraftOpacity] = useState(100);
  const [draftFontSize, setDraftFontSize] = useState(20);
  const [draftVideoWidth, setDraftVideoWidth] = useState(35);
  const [draftNowPlayingWidth, setDraftNowPlayingWidth] = useState(MIN_NOW_PLAYING_WIDTH);

  const load = useCallback(async () => {
    if (!channelId) return;
    setError(null);
    try {
      const { settings: loaded } = await api.getSettings(channelId);
      setSettings(loaded);
      setDraftOpacity(loaded.overlayVideoOpacity);
      setDraftFontSize(loaded.overlayFontSize);
      setDraftVideoWidth(loaded.overlayVideoWidth);
      setDraftNowPlayingWidth(loaded.overlayNowPlayingWidth);
      if (!loaded.overlayToken) {
        // First visit — provision the URL up front rather than making the
        // streamer click a button before there's anything to paste into OBS.
        const { overlayToken } = await api.regenerateOverlayToken(channelId);
        setSettings((s) => (s ? { ...s, overlayToken } : s));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [channelId]);

  // Drop the previous channel's settings on switch — same reasoning as
  // Settings.tsx — without re-flashing on every load() from a save.
  useEffect(() => {
    setSettings(null);
  }, [channelId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveLayout(layout: OverlayLayout) {
    if (!settings) return;
    setSettings({ ...settings, overlayLayout: layout }); // optimistic — it's just a corner
    try {
      await api.putSettings({ channelId, overlayLayout: layout });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load(); // roll back to the server's view
    }
  }

  async function saveFont(font: string) {
    if (!settings) return;
    setSettings({ ...settings, overlayFont: font });
    try {
      await api.putSettings({ channelId, overlayFont: font });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function saveVideoWidth(width: number) {
    if (!settings) return;
    setSettings({ ...settings, overlayVideoWidth: width });
    try {
      await api.putSettings({ channelId, overlayVideoWidth: width });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function saveNowPlayingWidth(width: number) {
    if (!settings) return;
    setSettings({ ...settings, overlayNowPlayingWidth: width });
    try {
      await api.putSettings({ channelId, overlayNowPlayingWidth: width });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function saveFontSize(size: number) {
    if (!settings) return;
    setSettings({ ...settings, overlayFontSize: size });
    try {
      await api.putSettings({ channelId, overlayFontSize: size });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function saveOpacity(opacity: number) {
    if (!settings) return;
    setSettings({ ...settings, overlayVideoOpacity: opacity });
    try {
      await api.putSettings({ channelId, overlayVideoOpacity: opacity });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function saveMuted(nextMuted: boolean) {
    if (!settings) return;
    if (!nextMuted) {
      const ok = window.confirm(
        "Unmuted audio plays through your streaming software's audio capture for this source. Only " +
          "enable this if you have the rights to play the requested audio — audible official music " +
          "on a live stream can trigger Content ID or a DMCA claim against the stream or VOD. Continue?",
      );
      if (!ok) return;
    }
    setSettings({ ...settings, overlayMuted: nextMuted });
    try {
      await api.putSettings({ channelId, overlayMuted: nextMuted });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function saveAutoplay(nextAutoplay: boolean) {
    if (!settings) return;
    setSettings({ ...settings, overlayAutoplay: nextAutoplay });
    try {
      await api.putSettings({ channelId, overlayAutoplay: nextAutoplay });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      void load();
    }
  }

  async function regenerate() {
    if (settings?.overlayToken) {
      const ok = window.confirm(
        "Regenerating invalidates the current URL — you'll need to update your streaming software's " +
          "browser source with the new URL afterward. Continue?",
      );
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    try {
      const { overlayToken } = await api.regenerateOverlayToken(channelId);
      setSettings((s) => (s ? { ...s, overlayToken } : s));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyUrl(url: string) {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!channelId) return null;
  if (!settings) {
    return (
      <div className="panel">
        {error ? <div className="error">{error}</div> : <StreamViewSkeleton />}
      </div>
    );
  }

  const overlayUrl = settings.overlayToken
    ? `${window.location.origin}/overlay/${settings.overlayToken}`
    : null;
  const nowPlaying = queue?.nowPlaying
    ? { title: queue.nowPlaying.title, youtubeUrl: queue.nowPlaying.youtubeUrl }
    : null;

  return (
    <div className="panel">
      {error && <div className="error">{error}</div>}

      <h2>Streaming overlay</h2>

      <div className="field grow">
        <label htmlFor="overlayUrl">Streaming browser-source URL</label>
        <div className="row" style={{ gap: 8 }}>
          <input
            id="overlayUrl"
            type="text"
            readOnly
            value={overlayUrl ?? "Generating…"}
            onFocus={(e) => e.target.select()}
            style={{ flex: "1 1 auto", minWidth: 0 }}
          />
          <button
            type="button"
            className="btn ghost icon row-action"
            onClick={() => overlayUrl && void copyUrl(overlayUrl)}
            disabled={!overlayUrl}
            title={copied ? "Copied!" : "Copy URL"}
            aria-label={copied ? "Copied!" : "Copy URL"}
          >
            {copied ? "✓" : "⧉"}
          </button>
          {overlayUrl ? (
            <a
              className="btn ghost icon row-action"
              href={overlayUrl}
              target="_blank"
              rel="noreferrer"
              title="Open in a new tab"
              aria-label="Open in a new tab"
            >
              ↗
            </a>
          ) : (
            <button
              type="button"
              className="btn ghost icon row-action"
              disabled
              title="Open in a new tab"
              aria-label="Open in a new tab"
            >
              ↗
            </button>
          )}
          <button
            type="button"
            className="btn ghost row-action"
            onClick={() => void regenerate()}
            disabled={!overlayUrl || busy}
          >
            Regenerate
          </button>
        </div>
      </div>

      <p className="muted panel-group panel-lead">
        Drag the boxes into a corner, or use the small corner buttons.
      </p>

      <div className="streamview-canvas">
        <OverlayCanvas
          layout={settings.overlayLayout}
          font={settings.overlayFont}
          fontSize={settings.overlayFontSize}
          nowPlayingWidth={settings.overlayNowPlayingWidth}
          nowPlaying={nowPlaying}
          mode="edit"
          muted={settings.overlayMuted}
          autoplay={settings.overlayAutoplay}
          videoOpacity={settings.overlayVideoOpacity}
          videoWidth={settings.overlayVideoWidth}
          onLayoutChange={saveLayout}
        />
      </div>

      <div className="panel-group row" style={{ alignItems: "flex-start" }}>
        <div className="field-group" style={{ flex: "1 1 260px" }}>
          <p className="panel-subhead">Now playing</p>
          <div className="row">
            <div className="field" style={{ maxWidth: 180 }}>
              <label htmlFor="overlayFont">Font</label>
              <select
                id="overlayFont"
                value={settings.overlayFont}
                onChange={(e) => void saveFont(e.target.value)}
              >
                {OVERLAY_FONTS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ maxWidth: 180 }}>
              <label htmlFor="overlayFontSize">Font size ({draftFontSize}px)</label>
              <input
                id="overlayFontSize"
                type="range"
                min={10}
                max={48}
                value={draftFontSize}
                onChange={(e) => setDraftFontSize(Number(e.target.value))}
                onPointerUp={() => void saveFontSize(draftFontSize)}
                onKeyUp={() => void saveFontSize(draftFontSize)}
              />
            </div>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <div className="field" style={{ maxWidth: 380 }}>
              <label htmlFor="overlayNowPlayingWidth">Box width ({draftNowPlayingWidth}px)</label>
              <input
                id="overlayNowPlayingWidth"
                type="range"
                min={MIN_NOW_PLAYING_WIDTH}
                max={MAX_NOW_PLAYING_WIDTH}
                value={draftNowPlayingWidth}
                onChange={(e) => setDraftNowPlayingWidth(Number(e.target.value))}
                onPointerUp={() => void saveNowPlayingWidth(draftNowPlayingWidth)}
                onKeyUp={() => void saveNowPlayingWidth(draftNowPlayingWidth)}
              />
            </div>
          </div>
        </div>

        <div className="field-group" style={{ flex: "1 1 260px" }}>
          <p className="panel-subhead">Video</p>
          <div className="row">
            <div className="field" style={{ maxWidth: 200 }}>
              <label htmlFor="overlayVideoWidth">Video size ({draftVideoWidth}%)</label>
              <input
                id="overlayVideoWidth"
                type="range"
                min={MIN_VIDEO_WIDTH}
                max={MAX_VIDEO_WIDTH}
                value={draftVideoWidth}
                onChange={(e) => setDraftVideoWidth(Number(e.target.value))}
                onPointerUp={() => void saveVideoWidth(draftVideoWidth)}
                onKeyUp={() => void saveVideoWidth(draftVideoWidth)}
              />
            </div>

            <div className="field" style={{ maxWidth: 200 }}>
              <label htmlFor="overlayOpacity">Video opacity ({draftOpacity}%)</label>
              <input
                id="overlayOpacity"
                type="range"
                min={10}
                max={100}
                value={draftOpacity}
                onChange={(e) => setDraftOpacity(Number(e.target.value))}
                onPointerUp={() => void saveOpacity(draftOpacity)}
                onKeyUp={() => void saveOpacity(draftOpacity)}
              />
            </div>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label className="row" style={{ alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={settings.overlayAutoplay}
                onChange={(e) => void saveAutoplay(e.target.checked)}
              />
              Autoplay video
            </label>
            <label className="row" style={{ alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={!settings.overlayMuted}
                onChange={(e) => void saveMuted(!e.target.checked)}
              />
              Play video audio (unmuted)
            </label>
            {!settings.overlayMuted && (
              <p className="warning">
                Audio is unmuted — this only autoplays reliably inside an actual streaming
                browser source (a plain browser tab will block it). Playing official music
                audibly on a live stream can trigger Content ID or a DMCA claim against the
                stream or VOD.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
